import React, {
  useEffect,
  useReducer,
  ReactNode,
  useMemo,
  useRef
} from 'react';
import { createContext, useContext } from '../selectableContext';
import ViewActions from './actions';
import { readStoredCinemaLevel } from '~/constants/cinema';
import ViewReducer, {
  AI_DISABLED_NOTICE,
  ViewState,
  ViewAction
} from './reducer';

interface ViewCtx {
  state: ViewState;
  actions: ReturnType<typeof ViewActions>;
  dispatch: React.Dispatch<ViewAction>;
}

export const ViewContext = createContext<ViewCtx | undefined>(undefined);

const pool: ViewState['exploreCategory'][] = [
  'subjects',
  'videos',
  'links',
  'ai-cards'
];
const getRandomCategory = (): ViewState['exploreCategory'] =>
  pool[Math.floor(Math.random() * pool.length)];

const initialViewState: ViewState = {
  pageVisible: true,
  exploreCategory: 'subjects',
  contentPath: '',
  contentNav: '',
  missionNav: '/missions',
  pageTitle: '',
  profileNav: '',
  boardNav: '/board',
  homeNav: '/',
  audioKey: '',
  aiFeaturesDisabled: true,
  aiFeaturesLoaded: false,
  aiDisabledNotice: AI_DISABLED_NOTICE,
  buildNavHidden: false,
  buildLandscapeMenusShown: false,
  videoCinemaLevel: 0,
  mutedBuildAppIds: [],
  buildAppNavTabIds: null,
  openBuildTab: null,
  buildAppToClose: null,
  buildAppToPin: null,
  killBuildAppSession: null
};

function initializeViewState(state: ViewState): ViewState {
  return {
    ...state,
    pageVisible:
      typeof document === 'undefined' || typeof document.hidden === 'undefined'
        ? true
        : !document.hidden,
    // the viewer's last chosen video theater tier, so a reload lands back in
    // the same layout
    videoCinemaLevel: readStoredCinemaLevel()
  };
}

export function ViewContextProvider({ children }: { children: ReactNode }) {
  const [viewState, viewDispatch] = useReducer(
    ViewReducer,
    initialViewState,
    initializeViewState
  );

  const didRandomise = useRef(false);
  useEffect(() => {
    if (!didRandomise.current && viewState.exploreCategory === 'subjects') {
      viewDispatch({
        type: 'SET_EXPLORE_CATEGORY',
        category: getRandomCategory()
      });
      didRandomise.current = true;
    }
  }, [viewState.exploreCategory]);

  const actions = useMemo(() => ViewActions(viewDispatch), []);

  const value = useMemo(
    () => ({ state: viewState, actions, dispatch: viewDispatch }),
    [viewState, actions]
  );

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
}

export const useViewContext = () => {
  const ctx = useContext(ViewContext);
  if (!ctx)
    throw new Error('useViewContext must be used within ViewContextProvider');
  return ctx;
};
